import { FC, useState } from "react";
import { Games } from "@/models/database/games/games.model";
import { Challenge } from "@/models/database/challenges/challenge.model";
import {
  Box,
  Flex,
  Text,
  Button,
  AspectRatio,
  Grid,
  Card,
  Spinner,
} from "@radix-ui/themes";
import { TimePicker } from "@/components/TimePicker/TimePicker";
import { Cross1Icon } from "@radix-ui/react-icons";
import ImageUploader from "@/components/ImageUploader/ImageUploader";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { timestamp } from "@/api/firebase/firebase.config";
import { Submission } from "@/models/database/submissions/Submission.model";
import { useSubmitScore } from "../../hooks/useSubmitScore";
import { siteRoutes } from "@/routes/siteRoutesList";
import { useNavigate } from "react-router-dom";
import { ErrorFallback } from "@/components";

interface SubmitScoreFormProps {
  challengeData: Challenge;
  gameData: Games;
}

export const SubmitScoreForm: FC<SubmitScoreFormProps> = ({
  challengeData,
  gameData,
}) => {
  const currentUser = useCurrentUserProfile((s) => s.user);
  const navigate = useNavigate();
  const { submitScore, loading, error } = useSubmitScore();

  const [values, setValues] = useState<Submission>({
    created: timestamp.now(),
    points: 0,
    type: gameData.submitScoreType,
    gameId: challengeData.gameType,
    screenShotUrl: "",
    challengeName: challengeData.name,
    challengeId: challengeData.slug,
    fieldToCompare: challengeData.challengeAdditionalData.fieldToCompare[0],
    status: "pending",
    uid: currentUser.uid,
    username: currentUser.userName,
    profileLogoUrl: currentUser.profileLogoUrl,
  });

  // Handle time change from picker
  const handleTimeChange = (totalMs: number) => {
    setValues((prev) => ({
      ...prev,
      points: totalMs,
    }));
  };

  const setNewValue = (targetName: string, value: string) => {
    return setValues((prev) => ({
      ...prev,
      [targetName]: value,
    }));
  };

  // Check if required fields are filled
  const isFormValid = () => {
    // Check if points is greater than 0
    const hasValidPoints = values.points > 0;
    // Check if screenshot URL exists
    const hasScreenshot = Boolean(values.screenShotUrl);

    return hasValidPoints && hasScreenshot;
  };

  // Handle form submission
  const handleSubmit = async () => {
    await submitScore(values);
    navigate(`${siteRoutes["challengesAndSlug"]}${challengeData.slug}`);
  };

  return (
    <Flex direction="column" gap="5">
      <Flex direction="column" gap="1">
        <Text as="label" size="2">
          What was your score?
        </Text>

        <TimePicker onChange={handleTimeChange} />
      </Flex>

      <Flex direction="column" gap="1">
        <Text as="label" size="2">
          Add a screenshot of your score
        </Text>
        {values.screenShotUrl ? (
          <Card my="3">
            <Grid columns={{ initial: "2", md: "5" }}>
              <Box position="relative">
                <AspectRatio ratio={1 / 1}>
                  <img
                    src={values.screenShotUrl + "&tr=w-100,h-100,q-70"}
                    alt="Image for upload"
                    style={{
                      objectFit: "cover",
                      width: "100%",
                      height: "100%",
                      borderRadius: "var(--radius-2)",
                    }}
                  />
                </AspectRatio>

                {/* Remove image button */}
                <Box position="absolute" inset="0.25rem 0.25rem auto auto">
                  <Button
                    variant="solid"
                    onClick={() => {
                      setNewValue("screenShotUrl", null);
                    }}
                  >
                    <Cross1Icon />
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Card>
        ) : (
          <ImageUploader
            location={`users/${currentUser.uid}/scores`}
            setImageUrl={(e) => {
              setNewValue("screenShotUrl", e);
            }}
            fieldText="Drop your image here, or click to browse"
            buttonText="Upload image"
          />
        )}
      </Flex>
      {error && <ErrorFallback>{error.message}</ErrorFallback>}

      <Flex gap="1">
        <Button
          size="3"
          disabled={!isFormValid()}
          variant="solid"
          onClick={handleSubmit}
        >
          {loading ? <Spinner /> : "Submit score"}
        </Button>
      </Flex>
    </Flex>
  );
};
